/**
 * File: todayOverviewUtils.ts
 * Path: apps/admin/src/features/today/utils/todayOverviewUtils.ts
 * Description: Everything the Today page shows, worked out from the records
 * the CRM already holds (the same input the Numbers snapshot adds up). Pure,
 * so the page and its tests share one rule. Four figures; money in by month
 * or week; one "needs attention" list that holds every follow-up, overdue
 * invoice (from the books), late payment not invoiced yet, unpaid deposit,
 * stale deal and care plan ending soon; and the
 * pipeline, quote and per-client numbers for the chosen dates. Money is whole
 * cents; the list is most urgent first.
 */

import {
  ADMIN_PATHS,
  LEAD_STAGE_LABELS,
  OPEN_LEAD_STAGES,
  RETENTION_MONTHS,
  contractOwedPayments,
  isRetentionFlagged,
  randToCents,
  retainerStats,
  type InvoiceLedger,
  type LeadStage,
} from "@idk/config";
import {
  buildNumbers,
  type NumbersInput,
  type NumbersSnapshot,
} from "@/shared/utils/numbersUtils";
import { buildToday, lastTouchedAt, type CrmSettings } from "@/shared/utils/todayListUtils";
import { type Period } from "@/shared/utils/numbersPeriodUtils";

export interface DayRange {
  from: string;
  to: string;
}

export type Grain = "month" | "week";
export type Tone = "red" | "amber" | "blue" | "green" | "neutral";

const DAY_MS = 86_400_000;
const utc = (day: string) => new Date(`${day}T00:00:00Z`);
const iso = (d: Date) => d.toISOString().slice(0, 10);
export const addDays = (day: string, n: number): string => iso(new Date(utc(day).getTime() + n * DAY_MS));
const dayNumber = (day: string) => Math.floor(utc(day).getTime() / DAY_MS);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Oct", or "Oct 25" for the first bucket and every January so a range over a year end reads clearly. */
const monthLabel = (start: string, first: boolean): string => {
  const name = MONTHS[Number(start.slice(5, 7)) - 1];
  return first || start.slice(5, 7) === "01" ? `${name} ${start.slice(2, 4)}` : name;
};

/** Monday of the week a day falls in. */
export const weekStart = (day: string): string => addDays(day, -((utc(day).getUTCDay() + 6) % 7));

const signed = (c: { status: string }): boolean => c.status === "client_signed" || c.status === "signed";

/** A short date: "9 Nov". */
export const dayMonth = (day: string): string => `${Number(day.slice(8, 10))} ${MONTHS[Number(day.slice(5, 7)) - 1]}`;

/** The first day of the books: the earliest date the range picker offers. */
export const BOOKS_START = "2023-03-01";

/** The Numbers input needs time, costs and monthly costs from this day on. */
export const loadFrom = (range: DayRange, today: string): string => {
  const lastWeek = addDays(weekStart(today), -7);
  return range.from < lastWeek ? range.from : lastWeek;
};

/** A period for the numbers snapshot out of two dates. */
export const periodOf = (range: DayRange, today: string): Period => {
  const last = range.to < today ? range.to : today;
  return {
    kind: "month",
    from: range.from,
    to: range.to,
    label: `${dayMonth(range.from)} to ${dayMonth(range.to)}`,
    elapsedDays: Math.max(0, dayNumber(last) - dayNumber(range.from) + 1),
  };
};

export interface FlowPoint {
  start: string;
  label: string;
  /** All money clients paid in this bucket. */
  inCents: number;
  /** Of that, what came from care plans. */
  outCents: number;
}

export interface AttentionRow {
  id: string;
  title: string;
  detail: string;
  tag: string;
  tone: Tone;
  href: string;
}

export interface BarRow {
  id: string;
  label: string;
  value: string;
  /** 0 to 100, as a share of the biggest bar. */
  share: number;
  href?: string;
}

export interface TodayFigures {
  owedCents: number;
  lateCount: number;
  lateCents: number;
  mrrCents: number;
  carePlanCount: number;
  openValueCents: number;
  openCount: number;
  /** Of the quotes decided in the dates, whole percent; null when none were. */
  winRatePercent: number | null;
  weekMinutes: number;
  lastWeekMinutes: number;
}

export interface TodayOverview {
  figures: TodayFigures;
  flow: FlowPoint[];
  attention: AttentionRow[];
  pipeline: BarRow[];
  quotes: BarRow[];
  clients: BarRow[];
  snapshot: NumbersSnapshot;
}

export const hoursText = (minutes: number): string => {
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} h`;
};

const share = (value: number, top: number): number => (top <= 0 ? 0 : Math.max(2, Math.min(100, Math.round((value / top) * 100))));

/** Money in, bucketed by month or week; every bucket in the range exists so a quiet month shows as nothing. */
export const moneyIn = (input: NumbersInput, range: DayRange, grain: Grain): FlowPoint[] => {
  const keyOf = (day: string) => (grain === "month" ? `${day.slice(0, 7)}-01` : weekStart(day));
  const carePlanContracts = new Set(input.retainers.flatMap((r) => (r.contractId ? [r.contractId] : [])));
  const points = new Map<string, FlowPoint>();
  const firstBucket = keyOf(range.from);
  const make = (start: string): FlowPoint => ({
    start,
    label: grain === "month" ? monthLabel(start, start === firstBucket) : dayMonth(start),
    inCents: 0,
    outCents: 0,
  });
  for (let day = keyOf(range.from), guard = 0; day <= range.to && guard < 400; guard += 1) {
    points.set(day, make(day));
    day = grain === "month" ? addDays(`${day.slice(0, 7)}-28`, 4).slice(0, 7) + "-01" : addDays(day, 7);
  }
  for (const [contractId, receipts] of Object.entries(input.receipts)) {
    for (const r of receipts) {
      if (r.receivedOn < range.from || r.receivedOn > range.to) continue;
      const point = points.get(keyOf(r.receivedOn));
      if (!point) continue;
      const cents = randToCents(r.amount);
      point.inCents += cents;
      if (carePlanContracts.has(contractId)) point.outCents += cents;
    }
  }
  return [...points.values()].sort((a, b) => a.start.localeCompare(b.start));
};

const isDeposit = (p: { reference: string; label: string }): boolean => /-DEP$/i.test(p.reference) || /deposit/i.test(p.label);

const rand = (cents: number): string => `R ${(cents / 100).toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export const buildTodayOverview = (
  input: NumbersInput,
  settings: CrmSettings,
  range: DayRange,
  grain: Grain,
  /** The books' invoices when they can be read: overdue invoices join the list, and take the place of the contract payments they bill. */
  ledger: Pick<InvoiceLedger, "invoices"> | null = null,
): TodayOverview => {
  const { today, now } = input;
  const contracts = input.contracts.filter(signed);
  const owed = contracts.flatMap((c) => contractOwedPayments(c, input.receipts[c.id] ?? [], input.dueDates[c.id] ?? [], today));
  const late = owed.filter((p) => p.dueOn !== null && p.daysLate > 0);
  const stats = retainerStats(input.retainers, input.contracts, now);
  const snapshot = buildNumbers(input, periodOf(range, today));

  // Open pipeline, in cents, as the pipeline shows it.
  const open = input.leads.filter((l) => !l.parked && OPEN_LEAD_STAGES.includes(l.stage));
  const byStage = (stage: LeadStage) => open.filter((l) => l.stage === stage);
  const stageCents = (stage: LeadStage) => byStage(stage).reduce((s, l) => s + randToCents(l.value ?? 0), 0);

  const thisWeek = weekStart(today);
  const lastWeek = addDays(thisWeek, -7);
  const minutesFrom = (from: string, to: string) =>
    input.time.filter((t) => t.date >= from && t.date <= to).reduce((s, t) => s + t.minutes, 0);

  const figures: TodayFigures = {
    owedCents: owed.reduce((s, p) => s + p.owedCents, 0),
    lateCount: late.length,
    lateCents: late.reduce((s, p) => s + p.owedCents, 0),
    mrrCents: stats.mrrCents,
    carePlanCount: stats.activeCount,
    openValueCents: open.reduce((s, l) => s + randToCents(l.value ?? 0), 0),
    openCount: open.length,
    winRatePercent: snapshot.sales.winRate.value === null ? null : Math.round(snapshot.sales.winRate.value),
    weekMinutes: minutesFrom(thisWeek, today),
    lastWeekMinutes: minutesFrom(lastWeek, addDays(thisWeek, -1)),
  };

  // ─── Needs attention: one list, most urgent first ───────────────────
  type Ranked = AttentionRow & { rank: number; order: number };
  const rows: Ranked[] = [];
  const invoices = ledger?.invoices ?? [];
  // An overdue invoice is the money to chase: it links to the invoice, the record the client is asked to pay.
  for (const inv of invoices) {
    if (inv.status !== "overdue" || inv.balanceCents <= 0) continue;
    rows.push({
      id: `invoice-${inv.number}`,
      title: `${inv.number} ${inv.party}`,
      detail: `${rand(inv.balanceCents)} not paid, was due ${dayMonth(inv.dueOn)}`,
      tag: `${inv.daysLate} day${inv.daysLate === 1 ? "" : "s"} late`,
      tone: "red",
      href: ADMIN_PATHS.invoice(inv.number),
      rank: 0,
      order: -inv.daysLate,
    });
  }
  // A contract payment the books have invoiced is chased through its invoice, so the same money is never listed twice.
  const invoiced = new Set(invoices.filter((i) => i.journalId !== null && i.status !== "void" && i.paymentReference).map((i) => i.paymentReference));
  const toChase = late.filter((p) => !invoiced.has(p.reference));
  // A late deposit is its own row. Every other late payment on a contract is one row, so a care plan six months behind is one line, not six.
  const lateByContract = new Map<string, typeof late>();
  for (const p of toChase) {
    if (isDeposit(p)) {
      rows.push({
        id: `deposit-${p.reference}`,
        title: `${p.clientName}: deposit not paid`,
        detail: `${rand(p.owedCents)} was due ${dayMonth(p.dueOn as string)}`,
        tag: "Unpaid",
        tone: "red",
        href: ADMIN_PATHS.money,
        rank: 1,
        order: -p.daysLate,
      });
    } else {
      lateByContract.set(p.contractId, [...(lateByContract.get(p.contractId) ?? []), p]);
    }
  }
  for (const [contractId, payments] of lateByContract) {
    const oldest = Math.max(...payments.map((p) => p.daysLate));
    const total = payments.reduce((sum, p) => sum + p.owedCents, 0);
    rows.push({
      id: `late-pay-${contractId}`,
      title: `${payments[0].contractReference} ${payments[0].clientName}`,
      detail: `${payments.length} payment${payments.length === 1 ? "" : "s"} late, ${rand(total)} in all`,
      tag: `${oldest} day${oldest === 1 ? "" : "s"} late`,
      tone: "red",
      href: ADMIN_PATHS.money,
      rank: 0,
      order: -oldest,
    });
  }
  const list = buildToday(input.leads, today, now, settings);
  for (const t of list.overdue) {
    rows.push({
      id: `late-${t.lead.id}`,
      title: `${t.lead.name}: follow-up was due`,
      detail: `${t.daysLate} day${t.daysLate === 1 ? "" : "s"} ago`,
      tag: "Overdue",
      tone: "red",
      href: ADMIN_PATHS.businesses.detail(t.lead.id),
      rank: 2,
      order: -t.daysLate,
    });
  }
  for (const t of list.dueToday) {
    rows.push({
      id: `due-${t.lead.id}`,
      title: `${t.lead.name}: follow-up is due`,
      detail: "Your next step for today",
      tag: "Today",
      tone: "amber",
      href: ADMIN_PATHS.businesses.detail(t.lead.id),
      rank: 3,
      order: 0,
    });
  }
  for (const s of list.stale) {
    rows.push({
      id: `stale-${s.lead.id}`,
      title: `${s.lead.name}: no move in ${s.daysQuiet} days`,
      detail: `Nobody has touched this ${LEAD_STAGE_LABELS[s.lead.stage].toLowerCase()} deal for ${s.daysQuiet} days (limit ${s.limit})`,
      tag: "Stale",
      tone: "red",
      href: ADMIN_PATHS.businesses.detail(s.lead.id),
      rank: 4,
      order: -s.daysQuiet,
    });
  }
  const soon = addDays(today, 30);
  for (const r of input.retainers) {
    if (r.status !== "active" || !r.endDate || r.endDate < today || r.endDate > soon) continue;
    rows.push({
      id: `plan-${r.id}`,
      title: `${r.kind} ends: ${r.clientName}`,
      detail: `${rand(r.monthlyCents)} a month stops on ${dayMonth(r.endDate)}`,
      tag: dayMonth(r.endDate),
      tone: "amber",
      href: ADMIN_PATHS.carePlans.root,
      rank: 5,
      order: dayNumber(r.endDate),
    });
  }
  for (const lead of list.needsAction) {
    rows.push({
      id: `next-${lead.id}`,
      title: `${lead.name}: needs a next step`,
      detail: "An open deal with nothing planned. Give it a step and a day.",
      tag: "No next step",
      tone: "neutral",
      href: ADMIN_PATHS.businesses.detail(lead.id),
      rank: 6,
      order: 0,
    });
  }
  // POPIA: lost or parked leads nobody has touched for a long time should have their details removed.
  for (const lead of input.leads) {
    if (!isRetentionFlagged({ stage: lead.stage, parked: lead.parked, consent: lead.consent, lastTouchedAt: lastTouchedAt(lead) }, now)) continue;
    rows.push({
      id: `old-${lead.id}`,
      title: `${lead.name}: old lead to remove`,
      detail: `Nobody has touched this lead for ${RETENTION_MONTHS} months. Open it and choose Remove this person's details.`,
      tag: "Remove",
      tone: "amber",
      href: ADMIN_PATHS.businesses.detail(lead.id),
      rank: 7,
      order: 0,
    });
  }
  rows.sort((a, b) => a.rank - b.rank || a.order - b.order || a.title.localeCompare(b.title));
  const attention: AttentionRow[] = rows.map((r) => ({ id: r.id, title: r.title, detail: r.detail, tag: r.tag, tone: r.tone, href: r.href }));

  // ─── Bottom row ─────────────────────────────────────────────────────
  const stageRows = (["new", "contacted", "talking", "quoted"] as const).map((stage) => ({ stage, cents: stageCents(stage), count: byStage(stage).length }));
  const topStage = Math.max(...stageRows.map((s) => s.cents), 0);
  const pipeline: BarRow[] = stageRows.map((s) => ({
    id: s.stage,
    label: `${LEAD_STAGE_LABELS[s.stage]} (${s.count})`,
    value: rand(s.cents),
    share: share(s.cents, topStage),
  }));

  const { winRate, dealSize } = snapshot.sales;
  const wonCount = snapshot.sales.conversion.find((c) => c.key === "won")?.count ?? 0;
  const quotes: BarRow[] = [
    {
      id: "win-rate",
      label: "Quotes won",
      value: winRate.value === null ? "Not enough yet" : `${Math.round(winRate.value)}% (${wonCount} won)`,
      share: winRate.value === null ? 0 : Math.max(2, Math.round(winRate.value)),
    },
    {
      id: "cost-to-win",
      label: "Cost to win a client",
      value: snapshot.value.cac.value === null ? "Not enough yet" : rand(snapshot.value.cac.value),
      share: snapshot.value.cac.value === null ? 0 : 25,
    },
    {
      id: "deal-size",
      label: "Average project",
      value: dealSize.value === null ? "Not enough yet" : rand(dealSize.value),
      share: dealSize.value === null ? 0 : 60,
    },
  ];

  const names = new Map<string, string>([
    ...input.clients.map((c) => [c.id, c.name] as const),
    ...contracts.filter((c) => c.clientId).map((c) => [c.clientId as string, c.content.client.name] as const),
  ]);
  const perClient = new Map<string, { revenue: number; cost: number; minutes: number }>();
  const entry = (id: string) => {
    const e = perClient.get(id) ?? { revenue: 0, cost: 0, minutes: 0 };
    perClient.set(id, e);
    return e;
  };
  for (const c of contracts) {
    if (!c.clientId) continue;
    for (const r of input.receipts[c.id] ?? []) {
      if (r.receivedOn >= range.from && r.receivedOn <= range.to) entry(c.clientId).revenue += randToCents(r.amount);
    }
  }
  for (const t of input.time) {
    if (t.recordKind !== "client" || t.date < range.from || t.date > range.to) continue;
    const e = entry(t.recordId);
    e.cost += t.costCents;
    e.minutes += t.minutes;
  }
  for (const c of input.costs) {
    if (c.recordKind === "client" && c.date >= range.from && c.date <= range.to) entry(c.recordId).cost += c.amountCents;
  }
  const clients: BarRow[] = [...perClient.entries()]
    .filter(([, e]) => e.revenue > 0 || e.minutes > 0)
    .sort((a, b) => b[1].revenue - a[1].revenue)
    .slice(0, 5)
    .map(([id, e]) => {
      const margin = e.revenue > 0 ? Math.round(((e.revenue - e.cost) / e.revenue) * 100) : null;
      return {
        id,
        label: names.get(id) ?? "Client",
        value: `${margin === null ? "No income" : `${margin}%`} · ${hoursText(e.minutes)}`,
        share: margin === null ? 0 : Math.max(2, Math.min(100, margin)),
        href: ADMIN_PATHS.businesses.detail(id),
      };
    });

  return { figures, flow: moneyIn(input, range, grain), attention, pipeline, quotes, clients, snapshot };
};
